import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { IMaskInput } from "react-imask";
import styled from "styled-components";
import { Colors } from "Components/Layout/Themes/Colors";
import { Resources, useResource } from "Translations/Resources";
import { getCurrencySymbol } from "Utils/CurrencyUtils";

const PageResources = Resources.Customer.Pay;
const MAX_AMOUNT = 10_000_000;

const StyledAmountInput = styled(IMaskInput)`
  font-size: 36px;
  font-weight: 400;
  line-height: 40px;
  height: 40px;
  color: ${Colors.Text};
  text-align: right;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  min-width: 0;
  /* Fallback for browsers without field-sizing: content
     (Safari < 18, older Android WebViews) — input keeps a reasonable
     fixed width instead of the browser default (~20 chars). */
  width: 6ch;

  @supports (field-sizing: content) {
    width: auto;
    field-sizing: content;
  }
`;

type Props = {
  value: number | undefined;
  currencyCode?: string;
  onChange: (value: number | undefined) => void;
  autoFocus?: boolean;
};

export const EditableAmountCard = ({
  value,
  currencyCode,
  onChange,
  autoFocus = true,
}: Props) => {
  const { t } = useResource();
  const currencySymbol = currencyCode ? getCurrencySymbol(currencyCode) : "";
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const handleAccept = (next: string) => {
    if (next === "") {
      onChange(undefined);
      return;
    }
    const parsed = Number(next.replace(",", "."));
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= MAX_AMOUNT) {
      onChange(parsed);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: Colors.White,
        borderRadius: "12px",
        padding: "20px 16px",
        boxShadow:
          " 0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Stack alignItems="center" gap={1}>
        <Typography
          fontSize={16}
          fontWeight={500}
          lineHeight="24px"
          color={Colors.DarkGrey}
          textAlign="center"
        >
          {t(PageResources.Amount)}
        </Typography>
        <Box
          display="flex"
          alignItems="baseline"
          justifyContent="center"
          gap={1}
        >
          <StyledAmountInput
            inputRef={inputRef}
            mask={Number}
            scale={2}
            radix=","
            mapToRadix={["."]}
            thousandsSeparator=" "
            unmask
            min={0}
            max={MAX_AMOUNT}
            lazy
            value={value?.toString() ?? ""}
            onAccept={handleAccept}
            inputMode="decimal"
            placeholder="0"
          />
          <Typography
            fontSize={36}
            fontWeight={400}
            lineHeight="40px"
            color={Colors.Text}
          >
            {currencySymbol}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
